import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { CheckCircle, AlertCircle, Search, X, PackageCheck, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_CFG = {
  submitted:  { cls: "bg-blue-100 text-blue-700",       label: "Pending Approval" },
  approved:   { cls: "bg-emerald-100 text-emerald-700", label: "Approved" },
  rejected:   { cls: "bg-red-100 text-red-600",         label: "Rejected" },
  in_transit: { cls: "bg-amber-100 text-amber-700",     label: "In Transit" },
  arrived:    { cls: "bg-violet-100 text-violet-700",   label: "Arrived at Warehouse" },
  validating: { cls: "bg-orange-100 text-orange-700",   label: "3PL Validating" },
  validated:  { cls: "bg-teal-100 text-teal-700",       label: "Validated" },
  completed:  { cls: "bg-green-100 text-green-700",     label: "Completed" },
};

function Badge({ status }) {
  const cfg = STATUS_CFG[status] || { cls: "bg-slate-100 text-slate-500", label: status };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

const STEPS = ["submitted","approved","in_transit","arrived","validating","validated","completed"];

function ProgressBar({ status }) {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-3">
      {STEPS.map((s, i) => (
        <div key={s} className={`h-1.5 flex-1 rounded-full transition-all ${i <= idx ? "bg-emerald-400" : "bg-slate-100"}`}/>
      ))}
    </div>
  );
}

export default function ClientInbound() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/inbound");
      setShipments(res.data.shipments || []);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    try {
      await api.patch(`/api/inbound/${id}/status`, { status: "approved" });
      flash("ASN approved. Vendor can now dispatch goods.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Reject this inbound shipment?")) return;
    try {
      await api.patch(`/api/inbound/${id}/status`, { status: "rejected" });
      flash("Shipment rejected.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const filtered = shipments.filter(s =>
    s.asnNumber?.toLowerCase().includes(search.toLowerCase()) ||
    s.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
    s.status?.toLowerCase().includes(search.toLowerCase())
  );

  const pending = shipments.filter(s => s.status === "submitted").length;
  const inTransit = shipments.filter(s => s.status === "in_transit").length;
  const completed = shipments.filter(s => s.status === "completed").length;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-teal-100 rounded-xl"><PackageCheck size={24} className="text-teal-600"/></div>
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Inbound Shipments</h1>
          <p className="text-slate-500 text-sm">Review and approve vendor shipments before they reach your warehouse</p>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Awaiting Approval", value: pending,   cls: pending > 0 ? "border-amber-200 bg-amber-50" : "" },
          { label: "In Transit",        value: inTransit, cls: "" },
          { label: "Completed",         value: completed, cls: "" },
        ].map((m, i) => (
          <div key={i} className={`bg-white rounded-2xl border border-slate-200 p-5 shadow-sm ${m.cls}`}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{m.label}</p>
            <p className="text-2xl font-bold text-navy-900">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-3">
        <Search size={16} className="text-slate-400"/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by ASN, vendor, or status..."
          className="flex-1 text-sm focus:outline-none text-slate-700 placeholder-slate-400"/>
        {search && <button onClick={() => setSearch("")}><X size={15} className="text-slate-400"/></button>}
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">
            No inbound shipments.
          </div>
        ) : filtered.map(s => (
          <div key={s.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${s.status === "submitted" ? "border-amber-200" : "border-slate-200"}`}>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="text-slate-400 hover:text-slate-600">
                    {expanded === s.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                  </button>
                  <div>
                    <p className="font-bold text-navy-900 font-mono">{s.asnNumber}</p>
                    <p className="text-xs text-slate-400">From: {s.vendorName} · {s.items?.length || 0} items · {s.expectedArrival ? `ETA ${s.expectedArrival}` : "No ETA"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge status={s.status}/>
                  {s.status === "submitted" && (
                    <>
                      <button onClick={() => handleApprove(s.id)}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700">
                        Approve
                      </button>
                      <button onClick={() => handleReject(s.id)}
                        className="px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600">
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
              <ProgressBar status={s.status}/>
            </div>

            {expanded === s.id && (
              <div className="border-t border-slate-100 px-6 py-4 bg-slate-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="text-left pb-2">SKU</th>
                      <th className="text-left pb-2">Description</th>
                      <th className="text-left pb-2">Material</th>
                      <th className="text-right pb-2">Expected Qty</th>
                      {(s.status === "validated" || s.status === "completed") && (
                        <><th className="text-right pb-2">Actual Qty</th><th className="text-right pb-2">Diff</th></>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(s.items || []).map((item, i) => (
                      <tr key={i}>
                        <td className="py-2 font-mono text-xs text-slate-500">{item.sku || "—"}</td>
                        <td className="py-2 text-slate-700">{item.description}</td>
                        <td className="py-2 text-slate-500 text-xs">{item.material || "—"}</td>
                        <td className="py-2 text-right text-slate-600">{item.qty} {item.unit}</td>
                        {(s.status === "validated" || s.status === "completed") && (
                          <>
                            <td className="py-2 text-right font-semibold">{item.actualQty ?? "—"}</td>
                            <td className={`py-2 text-right font-bold text-xs ${item.discrepancy > 0 ? "text-blue-600" : item.discrepancy < 0 ? "text-red-600" : "text-emerald-600"}`}>
                              {item.discrepancy != null ? (item.discrepancy > 0 ? `+${item.discrepancy}` : item.discrepancy) : "—"}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {s.notes && <p className="mt-3 text-xs text-slate-500"><span className="font-semibold">Vendor notes:</span> {s.notes}</p>}
                {s.inventoryUpdated && (
                  <p className="mt-2 text-xs text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle size={12}/> Inventory updated from this shipment</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
